import React, { useState, useRef } from 'react';
import { X, Upload, FileText, Save, AlertCircle, Loader2, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

interface UploadDamagePopupProps {
	isOpen: boolean;
	onClose: () => void;
	selectionCount: number;
	onSave: (files: File[]) => Promise<void>;
	isSaving: boolean;
}

export const UploadDamagePopup: React.FC<UploadDamagePopupProps> = ({
	isOpen,
	onClose,
	selectionCount,
	onSave,
	isSaving,
}) => {
	const { t } = useTranslation();
	const [isDragging, setIsDragging] = useState(false);
	const [files, setFiles] = useState<File[]>([]);
	const fileInputRef = useRef<HTMLInputElement>(null);

	if (!isOpen) return null;

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			const newFiles = Array.from(e.dataTransfer.files);
			setFiles((prev) => [...prev, ...newFiles]);
		}
	};

	const handleClickUpload = () => {
		fileInputRef.current?.click();
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			const newFiles = Array.from(e.target.files);
			setFiles((prev) => [...prev, ...newFiles]);

			e.target.value = '';
		}
	};

	const handleRemoveFile = (e: React.MouseEvent, indexToRemove: number) => {
		e.stopPropagation();
		setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
	};

	const handleSaveClick = async () => {
		if (files.length === 0) {
			toast.error(t('damages.upload.fileRequired'));
			return;
		}
		if (selectionCount === 0) {
			toast.error(t('damages.upload.selectionRequired'));
			return;
		}
		await onSave(files);
		setFiles([]);
	};

	const handleCancelClick = () => {
		setFiles([]);
		onClose();
	};

	return (
		<div
			className="absolute top-24 left-1/2 -translate-x-1/2 z-50 w-80 animate-in fade-in slide-in-from-top-4 duration-300 pointer-events-auto"
			onClick={(e) => e.stopPropagation()}
		>
			<Card className="shadow-2xl border-border bg-card/95 backdrop-blur-sm">
				<CardHeader className="p-3 pb-2 bg-muted/30 border-b border-foreground/20 rounded-t-lg">
					<CardTitle className="text-sm font-semibold flex justify-between items-center text-card-foreground">
						{t('damages.upload.title')}
						<Button
							variant="ghost"
							size="icon"
							className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
							onClick={onClose}
						>
							<X className="h-3 w-3" />
						</Button>
					</CardTitle>
				</CardHeader>
				<CardContent className="p-3 space-y-4">
					{/* Selection Count Indicator */}
					<div
						className={cn(
							'border rounded-md p-2 flex items-center gap-2 transition-colors',
							selectionCount > 0
								? 'bg-primary/10 border-primary/20 text-primary'
								: 'bg-warning/10 border-warning/30 text-warning'
						)}
					>
						<AlertCircle className="w-4 h-4 shrink-0" />
						<div className="text-xs font-medium">
							{selectionCount === 0
								? t('damages.upload.selectOnMap')
								: t('damages.upload.selected', { count: selectionCount })}
						</div>
					</div>

					{/* File Upload Area */}
					<div className="space-y-1">
						<Label className="text-xs text-muted-foreground">
							{t('damages.upload.attachments', { count: files.length })}
						</Label>
						<div
							className={cn(
								'border-2 border-dashed rounded-md flex flex-col items-center transition-all relative overflow-hidden',
								isDragging
									? 'border-primary bg-primary/10'
									: 'border-border hover:bg-muted/50 cursor-pointer',
								files.length > 0 ? 'h-40 items-start' : 'h-32 justify-center'
							)}
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
							onClick={handleClickUpload}
						>
							<input
								type="file"
								ref={fileInputRef}
								className="hidden"
								multiple
								onChange={handleFileChange}
							/>

							{files.length > 0 ? (
								<div className="w-full h-full overflow-y-auto p-2 space-y-2">
									{files.map((file, idx) => (
										<div
											key={`${file.name}-${idx}`}
											className="flex items-center justify-between bg-background border border-border p-2 rounded shadow-sm animate-in fade-in zoom-in duration-200"
										>
											<div className="flex items-center gap-2 overflow-hidden">
												<div className="bg-primary/10 p-1 rounded-full shrink-0">
													<FileText className="h-3 w-3 text-primary" />
												</div>
												<div className="flex flex-col min-w-0">
													<span className="text-[10px] font-semibold text-foreground truncate block max-w-[140px]">
														{file.name}
													</span>
													<span className="text-[9px] text-muted-foreground">
														{(file.size / 1024).toFixed(1)} KB
													</span>
												</div>
											</div>
											<Button
												variant="ghost"
												size="icon"
												className="h-5 w-5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
												onClick={(e) => handleRemoveFile(e, idx)}
											>
												<X className="h-3 w-3" />
											</Button>
										</div>
									))}
									<div className="flex justify-center pt-1 pb-1">
										<span className="text-[10px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 cursor-pointer">
											<Plus className="h-3 w-3" /> {t('damages.upload.addMoreFiles')}
										</span>
									</div>
								</div>
							) : (
								// EMPTY
								<div className="flex flex-col items-center p-4">
									<Upload className="h-6 w-6 text-muted-foreground mb-1" />
									<div className="text-[11px] text-muted-foreground text-center">
										<span className="font-semibold text-primary">{t('damages.upload.clickToBrowse')}</span>{' '}
										<br /> {t('damages.upload.dragFilesHere')}
									</div>
								</div>
							)}
						</div>
					</div>

					<div className="flex gap-2 pt-1">
						<Button
							size="sm"
							variant="outline"
							className="flex-1 h-8 text-xs"
							onClick={handleCancelClick}
							disabled={isSaving}
						>
							{t('common.actions.cancel')}
						</Button>
						<Button
							size="sm"
							className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 h-8 text-xs"
							onClick={handleSaveClick}
							disabled={selectionCount === 0 || files.length === 0 || isSaving}
						>
							{isSaving ? (
								<>
									<Loader2 className="h-3 w-3 mr-2 animate-spin" /> {t('common.states.saving')}
								</>
							) : (
								<>
									<Save className="h-3 w-3 mr-2" /> {t('common.actions.save')}{' '}
									{files.length > 0 && t('damages.upload.saveCount', { count: files.length })}
								</>
							)}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
};
